import React from 'react';

/*
  What OnHand shows when a screen has stopped.

  **Built 10 September 2026 for "Build the system states", job 25.** It is the
  one shape Group H adds, approved 5 September 2026, and the record for it is
  `docs/design/group-h/`. **Five of the thirteen system states are this card
  wearing different words**, which is the whole reason the group exists: OnHand
  used to hand-build a different layout for each one, and two of the five did
  not exist at all.

  Three parts, in this order, every time:

    1. What has happened.
    2. What it means for their work.
    3. What they can do now.

  **It never says what went wrong inside OnHand**, because that is not something
  a person can act on. The running app broke that rule in two places when the
  group was drawn: the Coming Soon page explained its own menu to the reader,
  and Movement Setup printed whatever the server said, verbatim, on screen.

  **It is deliberately the empty screen shape grown up.** `EmptyScreen.jsx` is
  what Groups F and G settled for "there is nothing here"; an empty screen and a
  stopped screen are the same sentence with a different cause, and drawing them
  differently would tell a person they are different kinds of thing when they do
  not care why the page is blank. Same mark in a soft square, same title size,
  same one sentence, same single way forward.

  **What it adds is the centring.** An empty screen sits inside the work area
  under the page bar, because the page around it is fine. A stop card is the
  whole of what is left, so it centres in whatever it is given. That needs its
  container to fill the height, which is what `.sy-fill` is for: it was found by
  looking rather than by measuring, because a card centred inside a box the
  height of the card measures perfectly and sits at the top of an empty screen.

  **Whether the shell stays is rule 35 and it is not this component's decision.**
  The caller places it. The app is still standing and only this page is not, so
  the menu is the way out: page not found, not yours to open, somebody has this
  count open. The app itself is not standing, so there is no menu to draw:
  opening OnHand, OnHand has stopped, being signed out.
*/

/* The marks. One per state, and each is a thing rather than a decoration: what
   it is, is in `docs/design/group-h/frame.py` with the reason beside it. */
const MARKS = {
  /* A magnifier that found nothing. That page is not here. */
  lost: <><circle cx="12" cy="12" r="9" /><path d="M8.5 8.5l7 7" /></>,
  /* A plug pulled out of a socket. OnHand has stopped. **Redrawn once during
     the design after looking at it**: the first version read as a squiggle at
     26 pixels while measuring perfectly. Rule 20. */
  stopped: <>
    <path d="M8.5 2v4.5" /><path d="M15.5 2v4.5" />
    <path d="M4.5 6.5h15v4a7.5 7.5 0 0 1-15 0z" /><path d="M12 18v4" />
  </>,
  /* A padlock, closed. Not yours to open, and somebody else has this open. */
  shut: <>
    <rect x="4" y="10.5" width="16" height="10" rx="2" />
    <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
  </>,
  /* A hammer. Not built yet. */
  building: <>
    <path d="M14.5 5.5 18 9l-8.5 8.5L6 14z" /><path d="M13 4l4 4" />
    <path d="M5.5 15.5 3 18l3 3 2.5-2.5" />
  </>,
};

const STROKE = {
  blue: 'var(--info)',
  gold: 'var(--accent-ink)',
  red: 'var(--critical)',
  grey: 'var(--text-secondary)',
};

/*
  `act` is the one way forward and `alt` is the second one where a state has
  two. **The primary is drawn last and the alternative first**, which looks
  backwards written down and is right on screen: on a desk they read
  alternative-then-primary left to right, and below 700 the row folds with the
  primary on top. That is the same fold Group E's dialog footer already uses,
  so a person meets one behaviour rather than two.
*/
export default function StopCard({ mark, tone = 'blue', title, says, act, alt, facts, fill = true }) {
  const card = (
    <div className="sy-card">
      <span className={'sy-mark ' + tone} style={{ color: STROKE[tone] }}>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true"
          stroke={STROKE[tone]} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {MARKS[mark]}
        </svg>
      </span>
      <div className="sy-title">{title}</div>
      {says && <div className="sy-says">{says}</div>}
      {facts && facts.length > 0 && (
        <div className="sy-facts">
          {facts.map((f) => (
            <span className="sy-fact" key={f.label}>
              <span className="sy-fact-n">{f.value}</span>
              <span className="sy-fact-l">{f.label}</span>
            </span>
          ))}
        </div>
      )}
      {(act || alt) && (
        <div className="sy-acts">
          {alt && (
            <button type="button" className="sy-act g" onClick={alt.onClick} disabled={alt.disabled}>
              {alt.label}
            </button>
          )}
          {act && (
            <button type="button" className="sy-act p" onClick={act.onClick} disabled={act.disabled}>
              {act.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
  return <div className={'sy-wrap' + (fill ? ' sy-fill' : '')}>{card}</div>;
}
